import { Component, Inject, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { ActiveCall, CallUpdates } from 'app/models/dto/call.model';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { take, takeUntil } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { Message } from 'app/messages/message-service/message.model';
import { ThreadReply, SendMessage, MessageAlertDismissed } from 'app/messages/message-store/message.actions';
import { Store, Select } from '@ngxs/store';
import { MessageState } from 'app/messages/message-store/message.state';
import { CallState } from 'app/store/call/call.state';
import { Alert } from 'app/models/dto/alert.model';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { isBefore, subMinutes, toDate } from 'date-fns';

@Component({
  selector: 'app-wrap-up-dialog',
  templateUrl: './wrap-up-dialog.component.html',
  styleUrls: ['./wrap-up-dialog.component.scss'],
})
export class WrapUpDialogComponent implements OnInit, OnDestroy {

  @Output() confirmedEndCall = new EventEmitter<CallUpdates>();
  @Output() updateEndedCall = new EventEmitter<CallUpdates>();

  @Select(MessageState.alert) alert$: Observable<Alert>;
  @Select(CallState.dispositionCall) dispositionCall$: Observable<ActiveCall>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  allowCloseWrapUpOption: boolean = true;
  callTag: string | null;
  callDisposition: string;
  endedCall: ActiveCall | null;
  reminderSet = false;
  noteLeft = false;
  noteRequired: boolean = false;
  smsSent = false;
  emailSent = false;
  businessAdded = false;
  businessUpdated = false;
  wrapUpOption: string | null = null;
  destroyed$ = new Subject<void>();

  callDispositionOptions = [
    { value: 'Made contact.', label: 'Made Contact' },
    { value: 'No answer.', label: 'No Answer' },
    { value: 'No answer, left voice mail message.', label: 'Left Voicemail' },
    { value: 'No answer, left message with person.', label: 'Left Msg w/Person' },
    { value: 'The call was disconnected.', label: 'Call Disconnected' },
    { value: 'Wrong number.', label: 'Wrong Number' },
    { value: 'Transferred the call.', label: 'Transferred Call' }
  ];

  defaultCallTagOptions = [
    { value: 'Merchant Interview' },
    { value: 'Expectations Call' },
    { value: 'Offer Presentation' },
    { value: 'Welcome Call' },
    { value: '2nd Voice' },
    { value: 'Doc Collection' },
  ];

  callTagOptions = this.defaultCallTagOptions;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      activeCall: ActiveCall;
      checkEndCall: boolean;
      borrowersInQueue: boolean;
      unknownCaller: boolean;
      unendedCall: boolean;
    },
    public dialog: MatDialog,
    private store: Store,
    private eventBusService: EventBusService,
  ) { }

  ngOnInit() {
    this.lenderUser$.pipe(take(1)).subscribe((lenderUser) => {
      this.checkCallTagOptions(lenderUser);
    });
    this.eventBusService.subscribe(this.eventBusService.types.AddedNewLeadEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.closeWrapUpOption());
    this.eventBusService.subscribe(this.eventBusService.types.CloseSendEmailViewEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        setTimeout(() => {
          this.closeWrapUpOption();
        }, 500);
      });
    this.eventBusService.subscribe(this.eventBusService.types.CloseMoveToExistingBusinessModalEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.closeWrapUpOption();
      });
    this.dispositionCall$.pipe(takeUntil(this.destroyed$)).subscribe(disCall => this.endedCall = disCall);
  }

  checkRequireNote() {
    const activeCall = this.data?.activeCall;
    const callStart = activeCall?.createdTimestamp || new Date(activeCall?.created || '');
    const hasBorrower = !!activeCall?.borrower;
    this.noteRequired = hasBorrower && isBefore(toDate(callStart), subMinutes(new Date(Date.now()), 2));
    this.allowCloseWrapUpOption = !this.noteRequired || this.wrapUpOption !== 'notes';
  }

  closeWrapUpOption() {
    switch (this.wrapUpOption) {
      case 'sms':
        this.smsSent = true;
      break;
      case 'email':
        this.emailSent = true;
      break;
      case 'notes':
        this.noteLeft = true;
      break;
      case 'newLead':
        this.businessAdded = true;
      break;
      case 'moveToExisting':
        this.businessUpdated = true;
      break;
      case 'reminder':
        this.reminderSet = true;
      break;
    }
    this.wrapUpOption = null;
  }

  checkCallTagOptions(lenderUser: LenderUser) {
    const validRoles = lenderUser.roles.filter( role => {
      return role === 'fundingDesk' || role.includes('CX');
    });
    if(validRoles.length) {
      this.callTagOptions = [
        ...this.defaultCallTagOptions,
        { value: 'Other' },
      ];
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  endCallConfirmation() {
    this.confirmedEndCall.emit({
        end: true
    });
    this.data.checkEndCall = false;
  }

  updateCallCloseDialog() {
    const callObject: CallUpdates = { disposition: this.callDisposition };
    if ( this.callDisposition === 'Made contact.') {
      callObject.callTag = this.callTag;
    }
    this.updateEndedCall.emit(callObject);
  }

  wrapUpNextStepsDisplay() {
    if (this.callDisposition && this.callDisposition !== 'Wrong number.') {
      if (this.callDisposition !== 'Made contact.' || (this.callDisposition === 'Made contact.' && this.callTag)) {
        return true;
      }
    return false;
    }
  }

  nextStepBtnDisplay(needBorrower: boolean) {
    if (this.data.activeCall && !this.data.unknownCaller) {
      if ((needBorrower && this.data.activeCall.borrower) || (!needBorrower && this.data.activeCall.borrowerId)) {
        if ((this.callDisposition === 'Made contact.' && this.callTag) || this.callDisposition !== 'Made contact.') {
          return true;
        }
      }
    }
    return false;
  }

  unknownCallerDisplay(addedBusiness: boolean) {
    if (this.data.unknownCaller) {
      if ((addedBusiness && !this.businessAdded) || (!addedBusiness && !this.businessUpdated)) {
        return true;
      }
    }
    return false;
  }

  displaySaveWrapUp() {
    return this.callDisposition &&
      (this.callDisposition !== 'Made contact.' || (this.callDisposition === 'Made contact.' && this.callTag));
  }

  handleCallTag(value: string) {
    this.callTag = value;
  }

  handleCallDisposition() {
    if (this.callDisposition !== 'Made contact.') {
      this.callTag = null;
    }
  }

  handleEmailCreate(message: Message): void {
    message.threadId ? this.store.dispatch(new ThreadReply(message.threadId, message)) : this.store.dispatch(new SendMessage(message));
  }

  handleAlertDismissed() {
    this.store.dispatch(new MessageAlertDismissed());
  }

}
